import { DisplayAgendaDate } from '../util/agenda-date';
import { flagMap } from './flags';
import { ProgramEvent } from '@cue/api';
import { StyledComponent } from '@cue/theme';
import { AvailableLanguages, Dayjs, TimeUtil, getAbbr } from '@cue/utility';
import { CSSObject } from '@emotion/react';
import styled from '@emotion/styled';
import { useConfig } from '@project/hooks/use-config';
import { useUser } from '@project/hooks/use-user';
import { availableLanguages } from '@project/i18n/config';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const AgendaEventTime: React.FC<
  AgendaEventTimeProps & StyledComponent<AgendaEventTimeCSSOverride>
> = (props) => {
  const {
    i18n: { language },
  } = useTranslation();
  const { start, end, showTimeZone, customTimeRenderer, eventLanguage, showLanguageFlag, isLive } =
    props;
  const eventUTCStart = TimeUtil.getUtc(start);
  const eventUTCEnd = TimeUtil.getUtc(end);

  const { preferredTimezone } = useUser();
  const { eventConfig } = useConfig();

  const displayTimezone = preferredTimezone || eventConfig.timezone;

  const eventTime = useMemo(() => {
    if (!start || !end) return;
    if (customTimeRenderer) {
      return customTimeRenderer({ start: eventUTCEnd, end: eventUTCEnd });
    }

    return (
      <>
        <div>
          {DisplayAgendaDate({ date: TimeUtil.convertToTimeZone(eventUTCStart, displayTimezone) })}
        </div>
        <div>{' | '}</div>
        <div>
          {TimeUtil.convertToTimeZone(eventUTCStart, displayTimezone).format('HH:mm')}
          {eventUTCStart &&
            ` - ${TimeUtil.convertToTimeZone(eventUTCEnd, displayTimezone).format('HH:mm')}`}
        </div>
      </>
    );
  }, [eventUTCStart, eventUTCEnd, displayTimezone, customTimeRenderer, end, start]);

  return (
    <Container className="cue-agenda-event-time-wrapper">
      <Time className="cue-agenda-event-time-time">{eventTime}</Time>
      {showTimeZone && start && (
        <TimeZone className="cue-agenda-event-time-timezone">
          {' '}
          {getAbbr(
            displayTimezone,
            (eventLanguage || language) as AvailableLanguages,
            TimeUtil.getUtc(eventUTCStart.format('YYYY-MM-DD')),
          )}
        </TimeZone>
      )}
      {showLanguageFlag && availableLanguages.length > 1 && (
        <div className="cue-agenda-event-flag">
          {flagMap?.[(eventLanguage || 'en') as keyof typeof flagMap]}
        </div>
      )}
      {isLive ? (
        <LiveMarker>
          <LiveDot />
          <span className="label">live</span>
        </LiveMarker>
      ) : null}
    </Container>
  );
};
export type AgendaEventTimeCSSOverride = {
  '.cue-agenda-event-time-wrapper'?: CSSObject;
  '.cue-agenda-event-time-time'?: CSSObject;
  '.cue-agenda-event-time-timezone'?: CSSObject;
};
export interface AgendaEventTimeProps extends Pick<ProgramEvent, 'start' | 'end'> {
  showTimeZone?: boolean;
  showLanguageFlag?: boolean;
  customTimeRenderer?({ start, end }: { start: Dayjs; end: Dayjs }): React.ReactNode;
  eventLanguage?: string;
  asString?: boolean;
  isLive?: boolean;
}

const Container = styled.div``;
const Time = styled.div``;
const TimeZone = styled.div``;
const LiveMarker = styled.div`
  width: 56.149px;
  height: 20px;
  margin-left: 20px;
  padding: 4px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #fff;
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 166.667% */
  border-radius: 25px;
  border: 1px solid #d80027;
  background: #d80027;

  .label {
    letter-spacing: 1px;
  }
`;

const LiveDot = () => {
  return (
    <Dot>
      <div className="heartbeat"></div>
      <div className="dot"></div>
    </Dot>
  );
};

const Dot = styled.div`
  position: relative;

  .dot {
    position: relative;
    background: #fff;
    border-radius: 10px;
    width: 6px;
    height: 6px;
  }

  .heartbeat {
    position: absolute;
    width: fit-content;
    background-color: #fff;
    border-radius: 10px;
    width: 6px;
    height: 6px;
    opacity: 0.75;
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  .label {
  }

  @keyframes ping {
    75%,
    100% {
      transform: scale(3);
      opacity: 0;
    }
  }
`;
